/**
 * Open a print window with an A4 grid of barcode labels for the given products.
 * Fixes the previous silent-failure by:
 *   (1) Calling doc.close() so onload actually fires,
 *   (2) Polling for JsBarcode until the CDN script is ready (network-safe),
 *   (3) Removing the fixed CSS height on the SVG so JsBarcode's own viewBox rules.
 *
 * Each template controls dimensions in millimetres.
 */

export const LABEL_TEMPLATES = {
  small:  { key: "small",  label: "40×25", w: 40, h: 25, cols: 4, nameFs: 9,  barH: 26, priceFs: 9,  barW: 1.1 },
  medium: { key: "medium", label: "50×30", w: 50, h: 30, cols: 3, nameFs: 11, barH: 40, priceFs: 11, barW: 1.4 },
  large:  { key: "large",  label: "70×40", w: 70, h: 40, cols: 2, nameFs: 13, barH: 55, priceFs: 13, barW: 1.8 },
};

export function printBarcodeLabels(products, tplKey) {
  const rows = (products || []).filter(Boolean);
  if (!rows.length) return { ok: false, reason: "no rows" };
  const tpl = LABEL_TEMPLATES[tplKey] || LABEL_TEMPLATES.medium;

  // Build a stable list of raw codes; keep 12-digit fallback (CODE128 encodes anything but must be non-empty).
  const codes = rows.map((p) => String(p.barcode || "").trim() || "0000000000");

  const cells = rows.map((p, i) => {
    const name = String(p.name || "").replace(/[<>&"']/g, (c) => ({"<":"&lt;",">":"&gt;","&":"&amp;",'"':"&quot;","'":"&#39;"}[c]));
    const priceNum = Number(p.sale_price || 0);
    const price = priceNum ? priceNum.toLocaleString("ar-EG") : "";
    const cur = String(p.currency || "SYP");
    return `<div class="label">
      <div class="name">${name}</div>
      <svg id="bc${i}" class="bc"></svg>
      <div class="price">${price} ${cur}</div>
    </div>`;
  }).join("");

  const w = window.open("", "_blank", "width=900,height=700");
  if (!w) return { ok: false, reason: "popup_blocked" };

  const html = `<!doctype html>
<html dir="rtl"><head><meta charset="utf-8"><title>ملصقات باركود (${rows.length}) — ${tpl.label} مم</title>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
<style>
  @page { size: A4; margin: 8mm; }
  body { font-family: Arial, "Segoe UI", sans-serif; margin: 0; padding: 4mm; background: #fff; color: #000; }
  .grid { display: grid; grid-template-columns: repeat(${tpl.cols}, 1fr); gap: 3mm; }
  .label { border: 1px dashed #cbd5e1; padding: 4px; text-align: center;
           width: ${tpl.w}mm; height: ${tpl.h}mm;
           display: flex; flex-direction: column; justify-content: space-between;
           align-items: center; overflow: hidden; page-break-inside: avoid;
           box-sizing: border-box; }
  .name { font-size: ${tpl.nameFs}px; font-weight: bold; line-height: 1.2;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap; width: 100%; }
  .bc { width: 100%; max-height: ${tpl.barH + 6}px; }
  .price { font-size: ${tpl.priceFs}px; font-weight: bold; color: #2563eb;
           font-variant-numeric: tabular-nums; }
  .status { position: fixed; top: 8px; inset-inline-end: 8px; font: 11px monospace;
            color: #64748b; background: #f1f5f9; padding: 3px 8px; border-radius: 4px; }
  @media print { .label { border-color: transparent } .status { display:none } }
</style>
</head><body>
<div class="status" id="status">جاري توليد الباركود…</div>
<div class="grid">${cells}</div>
<script>
(function () {
  var CODES = ${JSON.stringify(codes)};
  var CONFIG = { format: 'CODE128', width: ${tpl.barW}, height: ${tpl.barH},
                 displayValue: true, fontSize: ${Math.max(8, tpl.nameFs - 1)},
                 margin: 0, background: '#ffffff', lineColor: '#000000' };
  var TRIES = 0;
  function render() {
    if (typeof JsBarcode === 'undefined') {
      if (++TRIES > 60) { // ~6s max
        document.getElementById('status').textContent = 'تعذّر تحميل مكتبة الباركود — أعد المحاولة';
        return;
      }
      return setTimeout(render, 100);
    }
    var failed = 0;
    CODES.forEach(function (code, i) {
      var el = document.getElementById('bc' + i);
      if (!el) return;
      try { JsBarcode(el, code, CONFIG); }
      catch (e) { failed++; console.warn('barcode failed for', code, e); }
    });
    document.getElementById('status').textContent =
      'تم توليد ' + (CODES.length - failed) + '/' + CODES.length + ' — جاري الطباعة…';
    setTimeout(function () { window.focus(); window.print(); }, 350);
  }
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    render();
  } else {
    document.addEventListener('DOMContentLoaded', render);
  }
})();
</script>
</body></html>`;

  w.document.open();
  w.document.write(html);
  w.document.close(); // CRITICAL — without this, onload never fires in some browsers
  return { ok: true, count: rows.length, template: tpl.key };
}
